export enum VariantName {
  SUCCESS = 'success',
  INFO = 'info',
  WARNING = 'warning',
  ALERT = 'alert'
}

export enum ComponentType {
  BANNER = 'banner',
  POPUP = 'popup'
}

/** Backend / FE contract — serialized values match API `action.type` strings. */
export enum ButtonActionType {
  INTERNAL_ROUTE = 'internal_route',
  EXTERNAL_LINK = 'external_link',
  DISMISS = 'dismiss',
  CUSTOM = 'custom'
}

/**
 * Payload for `button.action`. Discriminated by `type`; only route/link/custom
 * variants carry `target` (dismiss has no payload).
 */
export type ButtonActionPayload =
  | { type: ButtonActionType.INTERNAL_ROUTE; target: string }
  | { type: ButtonActionType.EXTERNAL_LINK; target: string }
  | { type: ButtonActionType.DISMISS }
  | { type: ButtonActionType.CUSTOM; target: string };

export type ButtonAction = {
  text: string;
  variant?: string;
  action: ButtonActionPayload;
};

export interface BannerData {
  title: string;
  description: string;
  button?: ButtonAction;
}

export interface PopupData {
  title: string;
  description: string;
  primary_button: ButtonAction;
  secondary_button?: ButtonAction;
}

export interface StyleOverrides {
  icon_url?: string;
  image_url?: string;
  titleColor?: string;
  bgColor?: string;
  descriptionColor?: string;
  buttonBgColor?: string;
}

export interface BaseResolvedStyles {
  titleColor: string;
  bgColor: string;
  descriptionColor: string;
  buttonBgColor: string;
}

export interface BannerResolvedStyles extends BaseResolvedStyles {
  icon_url: string;
}

export interface PopupResolvedStyles extends BaseResolvedStyles {
  image_url: string;
}

export type ResolvedStyles = BannerResolvedStyles | PopupResolvedStyles;

export interface ComponentInstance<T = BannerData | PopupData> {
  id: string;
  name: string;
  variant: string;
  is_dismissible: boolean;
  is_one_time: boolean;
  data: T;
  configuration_overrides?: StyleOverrides;
  /** FE-only banners (e.g. KYC) — omit from dynamic-component Mixpanel. */
  is_frontend_static_banner?: boolean;
}

/** Instance after the slice resolves variant styles at ingestion time. */
export type ProcessedComponentInstance<
  T = BannerData | PopupData,
  S extends ResolvedStyles = ResolvedStyles
> = ComponentInstance<T> & { resolvedStyles: S };

export interface DynamicComponentGroup {
  type: string;
  page: string;
  instances: ComponentInstance[];
}

export interface DynamicComponentsResponse {
  components: DynamicComponentGroup[];
}

export interface DismissedComponentsStorage {
  [componentType: string]: string[];
}
